import {
  Typography as MuiTypography,
  Divider as MuiDivider,
} from "@material-ui/core";
import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";
import { Helmet } from "react-helmet-async";
import { z } from "zod";

import useQueryParams from "../../hooks/useQueryParams";
import FiltersForm from "../../components/FiltersForm";
import NutrientRatioChartContainer from "../../components/charts/nutrientRatioChart/NutrientRatioChartContainer";
import RawTable from "../../components/RawTable";
import { getWaterYearRange } from "../fieldDepthProfile/FieldDepthProfile";
import React from "react";

/* ---------- styled helpers ---------- */
const Section = styled.section`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
`;
const Typography = styled(MuiTypography)(spacing);
const Divider = styled(MuiDivider)(spacing);

/* ---------- defaults / schema ---------- */
const { start_date, end_date } = getWaterYearRange();

const LOCATION_OPTIONS = [
  { label: "CCR1 – Cherry Creek Res. Station 1", value: "79" },
  { label: "CCR2 – Cherry Creek Res. Station 2", value: "80" }, // ◀ default
  { label: "CCR3 – Cherry Creek Res. Station 3", value: "81" },
];

const DEFAULTS = {
  start_date,
  end_date,
  locationid: 80, // Drupal default = CCR 2
};

const schema = z.object({
  start_date: z.string().default(DEFAULTS.start_date),
  end_date: z.string().default(DEFAULTS.end_date),
  locationid: z.coerce.number().default(DEFAULTS.locationid),
});

/* ──────────────────────────────────────────────────────────── */

export default function NutrientRatios() {
  const { params, setParams } = useQueryParams(schema);

  const COLUMNS = [
    { title: "Date", field: "rdate", type: "date" },
    { title: "Location", field: "location_name" },
    { title: "TN:TP", field: "tn_tp", type: "numeric" },
    { title: "TDN:TDP", field: "tdn_tdp", type: "numeric" },
    { title: "TIN:SRP", field: "tin_srp", type: "numeric" },
    { title: "Chl-a", field: "chla", type: "numeric" },
    { title: "LMT", field: "lmt", type: "numeric" },
  ];

  const FILTERS = [
    {
      type: "date",
      param: "start_date",
      label: "Start Date",
      default: params.start_date,
    },
    {
      type: "date",
      param: "end_date",
      label: "End Date",
      default: params.end_date,
    },
    {
      type: "select", // single-select
      param: "locationid",
      label: "Surface-water location",
      default: String(params.locationid),
      options: LOCATION_OPTIONS,
    },
  ];

  return (
    <>
      <Helmet title="Nutrient Ratios" />
      <header>
        <Typography variant="h3" component="h1" gutterBottom>
          Nutrient Ratios and Chl-a in the Photic Zone
        </Typography>
        <Divider my={4} />
      </header>

      <main>
        {/* -- About Page --------------------------------------------------- */}
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            About this Page
          </Typography>
          <Typography variant="body1" gutterBottom>
            This tool tracks nutrient availability and light limitation by
            combining <strong>nitrogen-to-phosphorus ratios</strong> with{" "}
            <strong>Chlorophyll-a</strong> data in the reservoir's photic zone
            (the surface layer receiving sunlight).
          </Typography>
          <Typography variant="body1" gutterBottom>
            The three nutrient ratios shown are:
            <strong> TN:TP</strong> (Total Nitrogen to Total Phosphorus),
            <strong> TDN:TDP</strong> (Total Dissolved Nitrogen to Total
            Dissolved Phosphorus), and
            <strong> TIN:SRP</strong> (Total Inorganic Nitrogen to Soluble
            Reactive Phosphorus).
          </Typography>
          <Typography variant="body1" gutterBottom>
            These ratios can help determine whether algal growth is more likely
            limited by nitrogen or phosphorus at different times of year.
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Tip:</strong> As you adjust the filters, the chart and table
            update automatically. The page URL updates too, so you can{" "}
            <strong>bookmark</strong> or
            <strong> share a direct link</strong> to your current view.
          </Typography>
        </Section>

        {/* -- Filters ------------------------------------------------------ */}
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Filters
          </Typography>
          <FiltersForm config={FILTERS} onChange={setParams} />
        </Section>

        {/* -- Chart -------------------------------------------------------- */}
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Graph
          </Typography>
          <NutrientRatioChartContainer
            startDate={params.start_date}
            endDate={params.end_date}
            locationid={params.locationid}
          />
        </Section>

        {/* -- About Graph -------------------------------------------------- */}
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            About this Graph
          </Typography>
          <Typography variant="body1">
            This graph displays photic-zone concentrations of Chlorophyll-a{" "}
            <strong>(Chl-a)</strong> and light-limitation threshold{" "}
            <strong>(LMT)</strong>, alongside three nutrient ratios: Total
            Nitrogen to Total Phosphorus <strong>(TN:TP)</strong>, Total
            Dissolved Nitrogen to Total Dissolved Phosphorus{" "}
            <strong>(TDN:TDP)</strong>, and Total Inorganic Nitrogen to Soluble
            Reactive Phosphorus <strong>(TIN:SRP)</strong>. Nutrient ratios are
            shown as <strong>solid lines</strong> on the{" "}
            <strong>left y-axis</strong>, while Chl-a and LMT appear as{" "}
            <strong>dashed lines</strong> on the <strong>right y-axis</strong>.
            Use the filters above to change the date range and surface-water
            location. Hover over the graph to view exact values for each date.
          </Typography>
        </Section>

        {/* -- Raw table ---------------------------------------------------- */}
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Raw Data Table
          </Typography>
          <RawTable
            endPoint="depth-profile-additional-ratio-graph-data-crosstabbed"
            params={params}
            columns={COLUMNS}
          />
        </Section>
      </main>
    </>
  );
}
